import { onBeforeUnmount, reactive, ref } from 'vue';

/**
 * Drag interaction for the admin calendar (vue-cal), covering the two things the
 * built-in behaviour doesn't:
 *
 * 1. **A drop indicator.** vue-cal moves the event only once it is released, so until
 *    then there is nothing showing where it would land. We track the pointer and render
 *    a ghost block at the snapped target time (see CalendarDropIndicator).
 *
 * 2. **Touch dragging that doesn't eat the scroll.** vue-cal drags via the HTML5
 *    drag & drop API, which its own source notes is "not supported on touch devices" —
 *    yet on Android the browser still hijacks a touch that starts on a draggable
 *    element, so the day view refused to scroll unless the finger started on an empty
 *    spot. Events are therefore NOT natively draggable on touch; instead a long press
 *    lifts one, and the drag runs on our own touch handlers. A normal swipe is left
 *    alone and scrolls the day, as it should.
 */

/** Height (px) of one hour row, the time the grid starts at, and the grid's step. */
export interface CalendarGeometry {
  timeCellHeight: () => number;
  timeFromMinutes: () => number;
  timeStepMinutes: () => number;
}

export interface DropTarget {
  /** The `.vuecal__cell-content` under the pointer — the positioning context for events. */
  host: HTMLElement;
  /** Snapped start time, in minutes from midnight. */
  minutes: number;
}

/** Slot granularity of the indicator; matches vue-cal's own `snapToTime`. */
const SNAP_MINUTES = 15;
/** How long a finger must rest on an event before it lifts for dragging. */
const LONG_PRESS_MS = 350;
/** Movement (px) that cancels the long press — that gesture was a scroll, not a lift. */
const LONG_PRESS_TOLERANCE = 10;
/** Distance (px) from the scroller's edge where a drag starts auto-scrolling. */
const EDGE_SCROLL_ZONE = 64;
const EDGE_SCROLL_SPEED = 12;

const pad = (n: number) => String(n).padStart(2, '0');
export const minutesToLabel = (minutes: number): string =>
  `${pad(Math.floor(minutes / 60) % 24)}:${pad(Math.round(minutes % 60))}`;

/**
 * True when a finger is the *primary* pointer, which is where native HTML5 dragging is a
 * trap rather than a feature.
 *
 * Deliberately not "does this device have a touchscreen": Windows laptops with a touch
 * screen report `ontouchstart` and `maxTouchPoints > 0`, and disabling native dragging
 * there would take mouse drag-and-drop away from a desktop user. `pointer: coarse`
 * describes the primary input instead, so those machines keep the native drag while
 * phones and tablets get the long-press one.
 */
export const isTouchPrimary = (): boolean =>
  typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches
    // Very old webviews without matchMedia support: fall back to touch capability.
    ?? ('ontouchstart' in window || navigator.maxTouchPoints > 0));

export interface CalendarDragEvent {
  id: string | number;
  start: Date | string;
  end: Date | string;
  draggable?: boolean;
}

export function useCalendarDrag(options: {
  geometry: CalendarGeometry;
  /** Persist the move. Called once, on release, with the snapped start time. */
  onDrop: (event: CalendarDragEvent, minutes: number) => void;
}) {
  const preview = reactive({
    visible: false,
    host: null as HTMLElement | null,
    top: 0,
    height: 0,
    label: '',
  });

  /** The event being dragged with a finger (null while the native drag or nothing runs). */
  const touchDragEvent = ref<CalendarDragEvent | null>(null);
  /** The event a finger is resting on, once the long press lifted it. */
  const liftedEventId = ref<string | number | null>(null);

  // The event the native (desktop) drag started from, remembered from `event-focus`,
  // which vue-cal emits on mousedown — always before `dragstart`.
  let focusedEvent: CalendarDragEvent | null = null;
  /** Set between dragstart and dragend, so only a real event drag draws a preview. */
  let nativeDragActive = false;
  let longPressTimer: ReturnType<typeof setTimeout> | undefined;
  let pressOrigin = { x: 0, y: 0 };
  let grabOffsetMinutes = 0;
  let autoScrollFrame = 0;
  let autoScrollTarget: { el: HTMLElement; delta: number } | null = null;
  let lastDragEndedAt = 0;

  /**
   * True just after a touch drag finished. Lets the page ignore the gestures that
   * would otherwise ride on the same touch — the day swipe, and the tap that opens
   * the appointment.
   */
  const wasRecentlyDragging = () => Date.now() - lastDragEndedAt < 400;

  const durationMinutes = (event: CalendarDragEvent): number => {
    const start = new Date(event.start).getTime();
    const end = new Date(event.end).getTime();
    const minutes = Math.round((end - start) / 60000);
    return Number.isFinite(minutes) && minutes > 0 ? minutes : SNAP_MINUTES;
  };

  const startMinutesOf = (event: CalendarDragEvent): number => {
    const start = new Date(event.start);
    return start.getHours() * 60 + start.getMinutes();
  };

  const snap = (minutes: number): number => Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

  /**
   * Which cell column the pointer is over, and at what time. The indicator is
   * `pointer-events: none`, so it never shadows the element underneath.
   */
  const resolveTarget = (clientX: number, clientY: number): DropTarget | null => {
    const el = document.elementFromPoint(clientX, clientY);
    const host = (el as HTMLElement | null)?.closest?.('.vuecal__cell-content') as HTMLElement | null;
    if (!host) return null;

    const { timeCellHeight, timeFromMinutes, timeStepMinutes } = options.geometry;
    const rect = host.getBoundingClientRect();
    const rawMinutes = ((clientY - rect.top) / timeCellHeight()) * timeStepMinutes() + timeFromMinutes();
    return { host, minutes: rawMinutes };
  };

  /** Place the ghost at `minutes`, keeping it inside the day. */
  const showPreview = (target: DropTarget, event: CalendarDragEvent) => {
    const { timeCellHeight, timeFromMinutes, timeStepMinutes } = options.geometry;
    const duration = durationMinutes(event);
    // Subtract where the event was grabbed, so the block lands under the finger the way
    // it was picked up rather than jumping its top to the cursor.
    const start = Math.max(timeFromMinutes(), snap(target.minutes - grabOffsetMinutes));

    preview.host = target.host;
    preview.top = ((start - timeFromMinutes()) / timeStepMinutes()) * timeCellHeight();
    preview.height = Math.max((duration / timeStepMinutes()) * timeCellHeight(), 18);
    preview.label = `${minutesToLabel(start)} - ${minutesToLabel(start + duration)}`;
    preview.visible = true;
  };

  const hidePreview = () => {
    preview.visible = false;
    preview.host = null;
  };

  /** The snapped start time currently shown by the indicator. */
  const previewMinutes = (): number => {
    const { timeCellHeight, timeFromMinutes, timeStepMinutes } = options.geometry;
    return Math.round((preview.top / timeCellHeight()) * timeStepMinutes() + timeFromMinutes());
  };

  // ── Auto-scroll while dragging near the edges of the scrolling day column ──
  const stopAutoScroll = () => {
    if (autoScrollFrame) cancelAnimationFrame(autoScrollFrame);
    autoScrollFrame = 0;
    autoScrollTarget = null;
  };

  const runAutoScroll = () => {
    if (!autoScrollTarget) return stopAutoScroll();
    autoScrollTarget.el.scrollTop += autoScrollTarget.delta;
    autoScrollFrame = requestAnimationFrame(runAutoScroll);
  };

  const updateAutoScroll = (clientY: number, host: HTMLElement | null) => {
    const scroller = host?.closest('.vuecal__bg') as HTMLElement | null;
    if (!scroller) return stopAutoScroll();

    const rect = scroller.getBoundingClientRect();
    let delta = 0;
    if (clientY < rect.top + EDGE_SCROLL_ZONE) delta = -EDGE_SCROLL_SPEED;
    else if (clientY > rect.bottom - EDGE_SCROLL_ZONE) delta = EDGE_SCROLL_SPEED;

    if (!delta) return stopAutoScroll();
    autoScrollTarget = { el: scroller, delta };
    if (!autoScrollFrame) autoScrollFrame = requestAnimationFrame(runAutoScroll);
  };

  // ── Native (desktop) drag: vue-cal still performs the move; we only draw the target ──
  const onNativeDragStart = (e: DragEvent) => {
    const eventEl = (e.target as HTMLElement | null)?.closest?.('.vuecal__event') as HTMLElement | null;
    if (!eventEl || !focusedEvent) return;
    nativeDragActive = true;
    // Where in the event it was grabbed, so the ghost keeps the same offset.
    const { timeCellHeight, timeStepMinutes } = options.geometry;
    const rect = eventEl.getBoundingClientRect();
    grabOffsetMinutes = ((e.clientY - rect.top) / timeCellHeight()) * timeStepMinutes();
  };

  const onNativeDragOver = (e: DragEvent) => {
    if (!nativeDragActive || !focusedEvent) return;
    const target = resolveTarget(e.clientX, e.clientY);
    if (target) showPreview(target, focusedEvent);
    else hidePreview();
  };

  const onNativeDragEnd = () => {
    nativeDragActive = false;
    hidePreview();
    grabOffsetMinutes = 0;
  };

  /** Wire the native-drag listeners onto the calendar wrapper. */
  const bindNativeDrag = (root: HTMLElement) => {
    root.addEventListener('dragstart', onNativeDragStart, true);
    root.addEventListener('dragover', onNativeDragOver, true);
    root.addEventListener('dragend', onNativeDragEnd, true);
    root.addEventListener('drop', onNativeDragEnd, true);
    return () => {
      root.removeEventListener('dragstart', onNativeDragStart, true);
      root.removeEventListener('dragover', onNativeDragOver, true);
      root.removeEventListener('dragend', onNativeDragEnd, true);
      root.removeEventListener('drop', onNativeDragEnd, true);
    };
  };

  /** Remember the event a native drag will start from (vue-cal emits this on mousedown). */
  const onEventFocus = (event: CalendarDragEvent) => {
    focusedEvent = event;
  };

  // ── Touch drag: long press to lift, then move ──
  const cancelLongPress = () => {
    if (longPressTimer) clearTimeout(longPressTimer);
    longPressTimer = undefined;
  };

  const onEventTouchStart = (event: CalendarDragEvent, e: TouchEvent) => {
    if (!event?.draggable) return;
    const touch = e.touches[0];
    if (!touch) return;

    pressOrigin = { x: touch.clientX, y: touch.clientY };
    cancelLongPress();
    longPressTimer = setTimeout(() => {
      touchDragEvent.value = event;
      liftedEventId.value = event.id;
      // A short buzz to confirm the lift, where the platform offers one.
      navigator.vibrate?.(10);

      const eventEl = (e.target as HTMLElement | null)?.closest?.('.vuecal__event') as HTMLElement | null;
      const { timeCellHeight, timeStepMinutes } = options.geometry;
      const rect = eventEl?.getBoundingClientRect();
      grabOffsetMinutes = rect ? ((pressOrigin.y - rect.top) / timeCellHeight()) * timeStepMinutes() : 0;

      const target = resolveTarget(pressOrigin.x, pressOrigin.y);
      if (target) showPreview(target, event);
    }, LONG_PRESS_MS);
  };

  const onEventTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    if (!touchDragEvent.value) {
      // Still deciding: any real movement means the user is scrolling, not lifting.
      const moved = Math.abs(touch.clientX - pressOrigin.x) + Math.abs(touch.clientY - pressOrigin.y);
      if (moved > LONG_PRESS_TOLERANCE) cancelLongPress();
      return;
    }

    // Dragging for real — keep the page still while the block follows the finger.
    e.preventDefault();
    const target = resolveTarget(touch.clientX, touch.clientY);
    if (target) showPreview(target, touchDragEvent.value);
    updateAutoScroll(touch.clientY, target?.host ?? preview.host);
  };

  const onEventTouchEnd = () => {
    cancelLongPress();
    stopAutoScroll();

    const event = touchDragEvent.value;
    if (event) lastDragEndedAt = Date.now();
    const dropped = event && preview.visible;
    const minutes = dropped ? previewMinutes() : 0;

    touchDragEvent.value = null;
    liftedEventId.value = null;
    grabOffsetMinutes = 0;
    hidePreview();

    // Releasing without moving leaves the appointment where it was — no pointless save.
    if (dropped && event && minutes !== startMinutesOf(event)) options.onDrop(event, minutes);
  };

  onBeforeUnmount(() => {
    cancelLongPress();
    stopAutoScroll();
  });

  return {
    preview,
    liftedEventId,
    bindNativeDrag,
    wasRecentlyDragging,
    onEventFocus,
    onEventTouchStart,
    onEventTouchMove,
    onEventTouchEnd,
  };
}
